import AccountPicker from "../../components/AccountPicker";
import type { Dispatch, SetStateAction } from "react";
import type { DashboardData, Transaction } from "../../types";
import { advanceDueDate, dateLabel } from "../../lib/finance";
import { formatMoney } from "../../lib/money-math";
import FormActions from "../../components/FormActions";
import DateInput from "../../components/DateInput";
import PayeeField from "../../components/PayeeField";
import type { Need } from "../../lib/coverage";
import BillSourcePicker from "./BillSourcePicker";
import { dayAfter, type BillDraft } from "./bill-draft";

/** Adds or edits a bill or regular income. A new one can be based on an existing transaction. */
export default function BillForm({ draft, setDraft, data, ensure, busy, onSubmit, onCancel: _onCancel }: {
  draft: BillDraft;
  setDraft: Dispatch<SetStateAction<BillDraft | null>>;
  data: DashboardData;
  ensure: (need: Need) => unknown;
  busy: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const entryOptions = data.entryOptions || {
    categories: [], subcategories: [], subcategoriesByCategory: {},
    incomeCategories: [], incomeSubcategories: [], incomeSubcategoriesByCategory: {},
  };
  const accounts = data.accounts || [];
  const transactions = data.transactions || [];
  const categories = draft.type === "Income" ? entryOptions.incomeCategories : entryOptions.categories;
  const subcategories = (draft.type === "Income" ? entryOptions.incomeSubcategoriesByCategory : entryOptions.subcategoriesByCategory)[draft.category] || [];
  const sourceTransaction = draft.sourceTransactionId
    ? transactions.find((t) => t.id === draft.sourceTransactionId)
    : undefined;

  function pickSourceTransaction(t: Transaction) {
    const account = accounts.find((a) => a.id === t.accountId);
    setDraft((v) => v && {
      ...v,
      sourceTransactionId: t.id,
      type: t.type === "Income" ? "Income" : "Expense",
      accountId: t.accountId,
      currency: account?.currency || v.currency,
      payeeId: t.payeeId || v.payeeId,
      category: t.category || v.category,
      subcategory: t.subcategory || "",
      amount: t.amount ? formatMoney(t.amount) : v.amount,
      name: v.name || t.title || t.description || "",
    });
  }

  return (
    <form className="account-editor" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
      {!draft.id && (
        <BillSourcePicker
          transactions={transactions}
          searchLedger={() => ensure("all")}
          source={sourceTransaction}
          onPick={pickSourceTransaction}
          onClear={() => setDraft((v) => v && { ...v, sourceTransactionId: undefined, nextDueDate: "" })}
        />
      )}
      <label>
        Type
        <select
          value={draft.type}
          onChange={(e) => setDraft({ ...draft, type: e.target.value as BillDraft["type"], category: e.target.value === "Transfer" ? "" : (e.target.value === "Income" ? entryOptions.incomeCategories[0] : entryOptions.categories[0]) || "" })}
        >
          <option value="Expense">Money out (bill)</option>
          <option value="Income">Money in (income)</option>
          <option value="Transfer">Transfer</option>
        </select>
      </label>
      <label>{draft.type === "Income" ? "Income name" : "Bill name"}<input required autoFocus name="bill-name" autoComplete="off" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder={draft.type === "Income" ? "e.g. Salary" : "e.g. Rent"} /></label>
      <AccountPicker label="Account" accounts={accounts.filter(a => a.active !== false)} value={draft.accountId} onChange={(id) => { const acc = accounts.find((a) => a.id === id); setDraft({ ...draft, accountId: id, currency: acc?.currency || draft.currency }); }} required />
      {draft.type === "Transfer" && (
        <AccountPicker label="To account" accounts={accounts.filter(a => a.active !== false && a.id !== draft.accountId)} value={draft.destinationAccountId} onChange={(id) => setDraft({ ...draft, destinationAccountId: id })} required />
      )}
      {draft.type !== "Transfer" && (
        <>
          <label>
            <span className="field-label">Payee <small>Optional</small></span>
            <PayeeField
              payees={data.payees || []}
              value={draft.payeeId}
              pendingName={!draft.payeeId || draft.payeeId.startsWith("local:") ? draft.payeeName : undefined}
              onChange={(id, name) => setDraft({
                ...draft,
                payeeId: id,
                payeeName: id && !id.startsWith("local:") ? undefined : (name || ""),
              })}
            />
          </label>
          <div className="expense-fields">
            <label>
              Category
              <select required value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value, subcategory: "" })}>
                <option value="" disabled>Choose category</option>
                {categories.map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
            {subcategories.length > 0 && (
              <label>
                <span className="field-label">Subcategory <small>Optional</small></span>
                <select value={draft.subcategory} onChange={(e) => setDraft({ ...draft, subcategory: e.target.value })}>
                  <option value="">No subcategory</option>
                  {subcategories.map((s) => <option key={s}>{s}</option>)}
                </select>
              </label>
            )}
          </div>
        </>
      )}
      <label>
        <span className="field-label">Expected amount <small>Optional</small></span>
        <input inputMode="decimal" pattern="[0-9]+([.][0-9]{1,2})?" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} placeholder="0.00" />
      </label>
      <div className="expense-fields">
        <label>
          Frequency
          <select value={draft.frequency} onChange={(e) => setDraft({ ...draft, frequency: e.target.value as BillDraft["frequency"], ...(draft.id ? {} : { nextDueDate: "" }) })}>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </label>
        {!draft.id && !sourceTransaction && (
          <label>
            {draft.type === "Income" ? "First expected" : "First due"}
            <DateInput required value={draft.startDate} onChange={(e) => setDraft({ ...draft, startDate: e.target.value })} />
          </label>
        )}
      </div>
      {(draft.id || sourceTransaction) && (
        <label>
          {draft.type === "Income" ? "Next expected" : "Next due"}
          <DateInput
            required
            min={sourceTransaction ? dayAfter(sourceTransaction.date.slice(0, 10)) : undefined}
            value={draft.nextDueDate || (sourceTransaction ? advanceDueDate(sourceTransaction.date.slice(0, 10), draft.frequency) : "")}
            onChange={(e) => setDraft({ ...draft, nextDueDate: e.target.value })}
          />
        </label>
      )}
      {!draft.id && sourceTransaction && (
        <p className="muted">
          The {dateLabel(sourceTransaction.date)} transaction counts as already {draft.type === "Income" ? "received" : "paid"}. Change the date above if it normally falls on a different day.
        </p>
      )}
      <label>
        <span className="field-label">Remind me <small>Days before, optional</small></span>
        <input inputMode="numeric" pattern="[0-9]+" value={draft.reminderDaysBefore} onChange={(e) => setDraft({ ...draft, reminderDaysBefore: e.target.value })} placeholder="e.g. 3" />
      </label>
      <FormActions label="Save bill" busy={busy} />
    </form>
  );
}
